// --- src/components/UserManagement.tsx ---
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  User,
  Shield,
  Trash2,
  UserPlus,
  Ban,
  CheckCircle,
  Loader2,
  Printer,
  Mail,
  Upload,
  Pencil,
  X,
  Save,
  ShoppingCart,
} from 'lucide-react';
import { supabaseService } from '../services/supabaseService';
import { Colleague } from './types';
import { ConfirmDialog } from './ConfirmDialog';
import { getUserFriendlyError } from '../utils/errorHelper';
import { supabase } from '@/lib/supabaseClient';
import { logger } from '../services/logger';

const UserManagement: React.FC = () => {
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Confirmation dialog states
  const [toggleConfirm, setToggleConfirm] = useState<{ id: string; currentStatus: boolean } | null>(
    null
  );
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  // Edit role modal state
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    currentRole: 'admin' | 'employee';
  } | null>(null);
  const [newRole, setNewRole] = useState<'admin' | 'employee'>('employee');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Loading states for individual operations
  const [updatingPermissions, setUpdatingPermissions] = useState<string | null>(null);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'employee' as 'admin' | 'employee',
    barcode_id: '',
  });

  // Load colleagues and check admin status
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const adminStatus = await supabaseService.isCurrentUserAdmin();
      setIsAdmin(adminStatus);

      // Get current user ID for safety check
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }

      if (adminStatus) {
        const data = await supabaseService.getColleagues();
        setColleagues(data);
      }
    } catch (error: any) {
      logger.error('Error loading data', error, 'UserManagement');
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.full_name || !formData.email || !formData.password) {
      toast.error('Kérlek töltsd ki az összes mezőt!');
      return;
    }

    // Validate password meets Supabase requirements (min 6 chars)
    if (formData.password.length < 6) {
      toast.error('A jelszónak legalább 6 karakter hosszúnak kell lennie!');
      return;
    }

    // Log the data being sent (without password for security)
    const payloadToSend = {
      full_name: formData.full_name,
      email: formData.email,
      password: '***' + formData.password.length + ' chars***',
      role: formData.role,
    };
    console.log('🔵 [UserManagement] Sending user creation request:', payloadToSend);
    console.log('🔵 [UserManagement] Full name:', formData.full_name);
    console.log('🔵 [UserManagement] Email:', formData.email);
    console.log('🔵 [UserManagement] Password length:', formData.password.length);
    console.log('🔵 [UserManagement] Role:', formData.role);

    setSubmitting(true);
    try {
      const result = await supabaseService.createColleague({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        barcode_id: formData.barcode_id || undefined,
      });

      console.log('✅ [UserManagement] User created successfully:', result);

      // Reset form
      setFormData({
        full_name: '',
        email: '',
        password: '',
        role: 'employee',
        barcode_id: '',
      });

      // Reload the list to show the new user
      await loadData();

      // Show success message
      toast.success(`"${result.full_name}" sikeresen hozzáadva!`);
    } catch (error: any) {
      logger.error('Error creating colleague', error, 'UserManagement');
      logger.error('Error details', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = (id: string, currentStatus: boolean) => {
    setToggleConfirm({ id, currentStatus });
  };

  const handleToggleConfirm = async () => {
    if (!toggleConfirm) return;

    try {
      await supabaseService.updateColleagueStatus(toggleConfirm.id, !toggleConfirm.currentStatus);
      await loadData();
      toast.success(`Felhasználó státusza sikeresen frissítve!`);
      setToggleConfirm(null);
    } catch (error: any) {
      logger.error('Error updating status', error, 'UserManagement');
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
    }
  };

  const handleDelete = (id: string, name: string) => {
    setDeleteConfirm({ id, name });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      await supabaseService.deleteColleague(deleteConfirm.id);
      await loadData();
      toast.success('Felhasználó sikeresen törölve!');
      setDeleteConfirm(null);
    } catch (error: any) {
      logger.error('Error deleting colleague', error, 'UserManagement');
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
    }
  };

  const handleTogglePermission = async (
    userId: string,
    permission: 'access_labels' | 'access_email' | 'access_import' | 'access_pos',
    currentValue: boolean
  ) => {
    setUpdatingPermissions(userId);
    try {
      await supabaseService.updateUserPermissions(userId, { [permission]: !currentValue });
      await loadData();
      toast.success('Jogosultság sikeresen frissítve!');
    } catch (error: any) {
      console.error('Error updating permission:', error);
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
    } finally {
      setUpdatingPermissions(null);
    }
  };

  const handleEditRole = (colleague: Colleague) => {
    setEditingUser({
      id: colleague.id,
      name: colleague.full_name,
      currentRole: colleague.role,
    });
    setNewRole(colleague.role);
  };

  const handleSaveRole = async () => {
    if (!editingUser) return;

    // Safety check: prevent editing own role
    if (editingUser.id === currentUserId) {
      toast.error('Nem módosíthatod a saját szerepkörödet!');
      return;
    }

    // Don't update if role hasn't changed
    if (newRole === editingUser.currentRole) {
      setEditingUser(null);
      return;
    }

    setUpdatingRole(editingUser.id);
    try {
      // Optimistic update: update local state immediately
      setColleagues((prevColleagues) =>
        prevColleagues.map((colleague) =>
          colleague.id === editingUser.id ? { ...colleague, role: newRole } : colleague
        )
      );

      // Call the service to update in database
      await supabaseService.updateUserRole(editingUser.id, newRole);

      toast.success(`"${editingUser.name}" szerepköre sikeresen frissítve!`);
      setEditingUser(null);
    } catch (error: any) {
      // Rollback optimistic update on error
      await loadData();
      logger.error('Error updating role', error, 'UserManagement');
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
    } finally {
      setUpdatingRole(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2
            className="animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4"
            size={48}
          />
          <p className="text-slate-500 dark:text-gray-400">Betöltés...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <Shield className="mx-auto mb-4 text-red-600 dark:text-red-400" size={48} />
          <h2 className="text-xl font-bold text-red-800 dark:text-red-200 mb-2">
            Hozzáférés megtagadva
          </h2>
          <p className="text-red-600 dark:text-red-400">
            Csak adminisztrátorok férhetnek hozzá ehhez az oldalhoz.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="bg-slate-900 dark:bg-gray-950 rounded-xl shadow-lg p-6 border border-slate-800 dark:border-gray-800">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <User size={28} className="text-blue-400" />
          Munkatársak és Jogosultságok
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Csapat tagjainak kezelése és jogosultságok beállítása
        </p>
      </div>

      {/* Add New User Form */}
      <div className="bg-slate-900 dark:bg-gray-950 rounded-xl shadow-lg p-6 border border-slate-800 dark:border-gray-800">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <UserPlus size={20} className="text-blue-400" />
          Új Felhasználó Hozzáadása
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                Teljes Név
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 dark:bg-gray-800 border border-slate-700 dark:border-gray-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                placeholder="pl. Kovács János"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 dark:bg-gray-800 border border-slate-700 dark:border-gray-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                placeholder="kovacs.janos@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                Jelszó
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 dark:bg-gray-800 border border-slate-700 dark:border-gray-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                placeholder="Minimum 6 karakter"
                minLength={6}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                Szerepkör
              </label>
              <select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value as 'admin' | 'employee' })
                }
                className="w-full px-4 py-2 bg-slate-800 dark:bg-gray-800 border border-slate-700 dark:border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              >
                <option value="employee">Alkalmazott</option>
                <option value="admin">Adminisztrátor</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                Vonalkód Azonosító
              </label>
              <input
                type="text"
                value={formData.barcode_id}
                onChange={(e) => setFormData({ ...formData, barcode_id: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 dark:bg-gray-800 border border-slate-700 dark:border-gray-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                placeholder="pl. USER-1"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full md:w-auto px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Hozzáadás...
              </>
            ) : (
              <>
                <UserPlus size={18} />
                Hozzáadás
              </>
            )}
          </button>
        </form>
      </div>

      {/* Users List */}
      <div className="bg-slate-900 dark:bg-gray-950 rounded-xl shadow-lg border border-slate-800 dark:border-gray-800 overflow-hidden">
        <div className="p-6 border-b border-slate-800 dark:border-gray-800">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <User size={20} className="text-blue-400" />
            Felhasználók Listája ({colleagues.length})
          </h2>
        </div>

        {colleagues.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <User size={48} className="mx-auto mb-4 opacity-50" />
            <p>Még nincsenek felhasználók.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase">
                    Név / Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase">
                    Szerepkör
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase">
                    Vonalkód
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase">
                    Státusz
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-400 uppercase">
                    Jogosultságok
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-400 uppercase">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 dark:divide-gray-800">
                {colleagues.map((colleague) => (
                  <tr
                    key={colleague.id}
                    className="hover:bg-slate-800/50 dark:hover:bg-gray-900/50 transition"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-white font-medium">{colleague.full_name}</div>
                        <div className="text-slate-400 text-sm">{colleague.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                          colleague.role === 'admin'
                            ? 'bg-purple-900/50 text-purple-300 border border-purple-700'
                            : 'bg-slate-700 text-slate-300 border border-slate-600'
                        }`}
                      >
                        <Shield size={12} />
                        {colleague.role === 'admin' ? 'Adminisztrátor' : 'Alkalmazott'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-300 text-sm font-mono">
                        {colleague.barcode_id || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`flex items-center gap-1 text-sm font-medium ${
                          colleague.is_active ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {colleague.is_active ? (
                          <>
                            <CheckCircle size={16} />
                            Aktív
                          </>
                        ) : (
                          <>
                            <Ban size={16} />
                            Inaktív
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* Permission toggles - Only show for employees, admins always have access */}
                        {colleague.role === 'employee' ? (
                          <>
                            <button
                              onClick={() =>
                                handleTogglePermission(
                                  colleague.id,
                                  'access_labels',
                                  colleague.access_labels ?? false
                                )
                              }
                              disabled={updatingPermissions === colleague.id}
                              className={`p-2 rounded-lg transition relative ${
                                colleague.access_labels
                                  ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30'
                                  : 'bg-slate-700/50 text-slate-500 hover:bg-slate-700'
                              }`}
                              title={
                                colleague.access_labels ? 'Címkék: Engedélyezve' : 'Címkék: Tiltva'
                              }
                            >
                              {updatingPermissions === colleague.id ? (
                                <Loader2 className="animate-spin" size={16} />
                              ) : (
                                <Printer size={16} />
                              )}
                            </button>
                            <button
                              onClick={() =>
                                handleTogglePermission(
                                  colleague.id,
                                  'access_email',
                                  colleague.access_email ?? false
                                )
                              }
                              disabled={updatingPermissions === colleague.id}
                              className={`p-2 rounded-lg transition ${
                                colleague.access_email
                                  ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30'
                                  : 'bg-slate-700/50 text-slate-500 hover:bg-slate-700'
                              }`}
                              title={
                                colleague.access_email ? 'Email: Engedélyezve' : 'Email: Tiltva'
                              }
                            >
                              {updatingPermissions === colleague.id ? (
                                <Loader2 className="animate-spin" size={16} />
                              ) : (
                                <Mail size={16} />
                              )}
                            </button>
                            <button
                              onClick={() =>
                                handleTogglePermission(
                                  colleague.id,
                                  'access_import',
                                  colleague.access_import ?? false
                                )
                              }
                              disabled={updatingPermissions === colleague.id}
                              className={`p-2 rounded-lg transition ${
                                colleague.access_import
                                  ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30'
                                  : 'bg-slate-700/50 text-slate-500 hover:bg-slate-700'
                              }`}
                              title={
                                colleague.access_import
                                  ? 'Adatbázis: Engedélyezve'
                                  : 'Adatbázis: Tiltva'
                              }
                            >
                              {updatingPermissions === colleague.id ? (
                                <Loader2 className="animate-spin" size={16} />
                              ) : (
                                <Upload size={16} />
                              )}
                            </button>
                            <button
                              onClick={() =>
                                handleTogglePermission(
                                  colleague.id,
                                  'access_pos',
                                  colleague.access_pos ?? false
                                )
                              }
                              disabled={updatingPermissions === colleague.id}
                              className={`p-2 rounded-lg transition ${
                                colleague.access_pos
                                  ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30'
                                  : 'bg-slate-700/50 text-slate-500 hover:bg-slate-700'
                              }`}
                              title={
                                colleague.access_pos
                                  ? 'POS Terminal: Engedélyezve'
                                  : 'POS Terminal: Tiltva'
                              }
                            >
                              {updatingPermissions === colleague.id ? (
                                <Loader2 className="animate-spin" size={16} />
                              ) : (
                                <ShoppingCart size={16} />
                              )}
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-slate-500 italic">
                            Admin - Minden jogosultság
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditRole(colleague)}
                          disabled={updatingRole === colleague.id || colleague.id === currentUserId}
                          className="p-2 text-blue-400 hover:bg-blue-900/20 hover:text-blue-300 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            colleague.id === currentUserId
                              ? 'Nem módosíthatod a saját szerepkörödet'
                              : 'Szerepkör szerkesztése'
                          }
                        >
                          {updatingRole === colleague.id ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : (
                            <Pencil size={18} />
                          )}
                        </button>
                        <button
                          onClick={() => handleToggleStatus(colleague.id, colleague.is_active)}
                          className={`p-2 rounded-lg transition ${
                            colleague.is_active
                              ? 'text-red-400 hover:bg-red-900/20 hover:text-red-300'
                              : 'text-green-400 hover:bg-green-900/20 hover:text-green-300'
                          }`}
                          title={colleague.is_active ? 'Deaktiválás' : 'Aktiválás'}
                        >
                          {colleague.is_active ? <Ban size={18} /> : <CheckCircle size={18} />}
                        </button>
                        <button
                          onClick={() => handleDelete(colleague.id, colleague.full_name)}
                          className="p-2 text-red-400 hover:bg-red-900/20 hover:text-red-300 rounded-lg transition"
                          title="Törlés"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Role Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-slate-900 dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-700 dark:border-gray-700 animate-in fade-in zoom-in duration-200">
            <div className="bg-blue-600 dark:bg-blue-500 p-4 flex justify-between items-center text-white">
              <h3 className="font-bold flex items-center gap-2">
                <Pencil size={18} /> Szerepkör Szerkesztése
              </h3>
              <button
                onClick={() => setEditingUser(null)}
                className="hover:bg-blue-700 dark:hover:bg-blue-600 p-1 rounded"
                aria-label="Bezárás"
                disabled={updatingRole === editingUser.id}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 dark:text-gray-400 uppercase mb-1">
                  Felhasználó
                </label>
                <input
                  type="text"
                  value={editingUser.name}
                  disabled
                  className="w-full px-4 py-2 bg-slate-800 dark:bg-gray-700 border border-slate-700 dark:border-gray-600 rounded-lg text-slate-400 dark:text-gray-400 font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 dark:text-gray-400 uppercase mb-1">
                  Szerepkör
                </label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as 'admin' | 'employee')}
                  disabled={updatingRole === editingUser.id}
                  className="w-full px-4 py-2 bg-slate-800 dark:bg-gray-700 border border-slate-700 dark:border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-accent focus:border-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="employee">Alkalmazott</option>
                  <option value="admin">Adminisztrátor</option>
                </select>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  disabled={updatingRole === editingUser.id}
                  className="flex-1 py-2 text-slate-400 dark:text-gray-300 hover:bg-slate-800 dark:hover:bg-gray-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Mégse
                </button>
                <button
                  onClick={handleSaveRole}
                  disabled={updatingRole === editingUser.id || newRole === editingUser.currentRole}
                  className="flex-1 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {updatingRole === editingUser.id ? (
                    <>
                      <Loader2 className="animate-spin" size={18} /> Mentés...
                    </>
                  ) : (
                    <>
                      <Save size={18} /> Mentés
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={!!toggleConfirm}
        title={toggleConfirm?.currentStatus ? 'Felhasználó Deaktiválása' : 'Felhasználó Aktiválása'}
        message={
          toggleConfirm?.currentStatus
            ? `Biztosan deaktiválod ezt a felhasználót? A felhasználó nem fog tudni bejelentkezni.`
            : `Biztosan aktiválod ezt a felhasználót? A felhasználó újra be tud jelentkezni.`
        }
        confirmText={toggleConfirm?.currentStatus ? 'Deaktiválás' : 'Aktiválás'}
        cancelText="Mégse"
        variant="warning"
        onConfirm={handleToggleConfirm}
        onCancel={() => setToggleConfirm(null)}
      />

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="Felhasználó Törlése"
        message={`Biztosan törölni szeretnéd a következő felhasználót: "${deleteConfirm?.name}"?\n\nEz a művelet nem visszavonható!`}
        confirmText="Törlés"
        cancelText="Mégse"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default UserManagement;
